import { useApiClient, usePost, usePut } from "@albertine/api-client";
import {
    type CatalogItemImageReference,
    type FileUploadRejectionReason,
    type FinalizeFileUploadData,
    type InitiateFileUploadData,
    type InitiateFileUploadRequest,
    SuccessResponseGetFileUploadSchema,
} from "@albertine/types/openapi/lmt";
import { toApiErrorMessage } from "@/common/utils/apiError.util";
import type { CatalogItemFormSchema } from "../../common/CatalogItem";
import {
    imageFileMimeType,
    requireSupportedImageFile,
} from "../../common/utils/image.util";
import {
    toCreateCatalogItemRequest,
    toUpdateCatalogItemRequest,
} from "./catalogItemRequest";
import type { ImageReference } from "./ImageReference";

const UPLOAD_POLL_INTERVAL_MS = 1000;
const UPLOAD_POLL_TIMEOUT_MS = 60_000;

type SaveCatalogItemInput = {
    catalogItemId: string | undefined;
    form: CatalogItemFormSchema;
    imageReferences: ImageReference[];
};

export function useSaveCatalogItem() {
    const { apiClient } = useApiClient();
    const { mutateAsync: createCatalogItem } = usePost("createCatalogItem");
    const { mutateAsync: updateCatalogItem } = usePut("updateCatalogItem");
    const { mutateAsync: initiateCatalogFileUpload } = usePost("initiateLmtFileUpload");
    const { mutateAsync: finalizeCatalogFileUpload } = usePost("finalizeLmtFileUpload");

    async function saveCatalogItem({
        catalogItemId,
        form,
        imageReferences,
    }: SaveCatalogItemInput) {
        const images = await Promise.all(
            imageReferences.map((reference) =>
                resolveImageReference(reference, {
                    initiateCatalogFileUpload,
                    finalizeCatalogFileUpload,
                    readFileUpload: (fileUploadId) =>
                        readFileUploadStatus(apiClient, fileUploadId),
                }),
            ),
        );

        if (catalogItemId) {
            return updateCatalogItem({
                param: { id: catalogItemId },
                json: toUpdateCatalogItemRequest(form, images),
            });
        }

        return createCatalogItem({
            json: toCreateCatalogItemRequest(form, uploadIds(images)),
        });
    }

    return { saveCatalogItem };
}

type UploadClient = {
    initiateCatalogFileUpload: (input: {
        json: InitiateFileUploadRequest;
    }) => Promise<InitiateFileUploadData>;
    finalizeCatalogFileUpload: (input: {
        param: { id: string };
    }) => Promise<FinalizeFileUploadData>;
    readFileUpload: (fileUploadId: string) => Promise<void>;
};

async function resolveImageReference(
    reference: ImageReference,
    client: UploadClient,
): Promise<CatalogItemImageReference> {
    switch (reference.type) {
        case "library":
            return { type: "existing", imageId: reference.file.id };
        case "local":
            return {
                type: "upload",
                fileUploadId: await uploadLocalImage(reference.file, client),
            };
        case "external":
            return {
                type: "upload",
                fileUploadId: await importImageFromUrl(reference.url, client),
            };
        default: {
            const unhandled: never = reference;
            return unhandled;
        }
    }
}

function uploadIds(images: readonly CatalogItemImageReference[]): string[] {
    return images.map((image) => {
        switch (image.type) {
            case "upload":
                return image.fileUploadId;
            case "existing":
                throw new Error(
                    "Existing images can only be kept when updating a catalog item",
                );
            default: {
                const unhandled: never = image;
                return unhandled;
            }
        }
    });
}

async function uploadLocalImage(file: File, client: UploadClient): Promise<string> {
    requireSupportedImageFile(file);
    const mimeType = imageFileMimeType(file);
    const initiated = await client.initiateCatalogFileUpload({
        json: {
            source: "client",
            uploadPurpose: {
                type: "catalog-image",
            },
            mimeType,
            fileName: file.name,
        },
    });
    if (initiated.source !== "client") {
        throw new Error("Expected a client file upload");
    }

    const transfer = await fetch(initiated.uploadUrl, {
        method: initiated.uploadHttpMethod,
        headers: initiated.uploadHeaders,
        body: file,
    });
    if (!transfer.ok) {
        throw new Error(`Uploading the image failed (${transfer.status})`);
    }

    const finalized = await client.finalizeCatalogFileUpload({
        param: { id: initiated.id },
    });
    if (finalized.status === "rejected") {
        throw new Error("The image was rejected");
    }
    return initiated.id;
}

async function importImageFromUrl(url: string, client: UploadClient): Promise<string> {
    const initiated = await client.initiateCatalogFileUpload({
        json: {
            source: "url",
            uploadPurpose: {
                type: "catalog-image",
            },
            url,
        },
    });
    if (initiated.source !== "url") {
        throw new Error("Expected a URL file upload");
    }

    await client.readFileUpload(initiated.id);
    return initiated.id;
}

type CatalogFileUploadClient = ReturnType<typeof useApiClient>["apiClient"];

async function readFileUploadStatus(
    apiClient: CatalogFileUploadClient,
    fileUploadId: string,
): Promise<void> {
    const startedAt = Date.now();
    while (Date.now() - startedAt < UPLOAD_POLL_TIMEOUT_MS) {
        const response = await apiClient.get.getLmtFileUpload({
            param: { id: fileUploadId },
        });
        const body: unknown = await response.json();
        const parsed = SuccessResponseGetFileUploadSchema.safeParse(body);
        if (!parsed.success) {
            throw new Error(
                toApiErrorMessage(body, "Could not check the image upload"),
            );
        }

        switch (parsed.data.data.status) {
            case "finalized":
                return;
            case "rejected":
                throw new Error(rejectionMessage(parsed.data.data.rejectionReason));
            case "pending":
                await delay(UPLOAD_POLL_INTERVAL_MS);
                break;
            default: {
                const unhandled: never = parsed.data.data.status;
                throw new Error(`Unexpected image upload status: ${unhandled}`);
            }
        }
    }

    throw new Error("The image import did not finish in time");
}

function rejectionMessage(reason: FileUploadRejectionReason | null): string {
    switch (reason) {
        case "invalid-file-type":
        case "invalid-mime-type":
            return "One of the images is not a supported file type";
        case "upload-timed-out":
            return "One of the images took too long to upload";
        case "url-not-allowed":
            return "One of the image URLs is not allowed";
        case "download-failed":
            return "One of the images could not be downloaded";
        case null:
            return "One of the images was rejected";
        default: {
            const unhandled: never = reason;
            return unhandled;
        }
    }
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}
